import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./TeamList.css";
import API_URL from "./config";
import useTeamTheme from "./useTeamTheme";

// Keep roster OVR aligned with Dashboard and gameplay screens.
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.injury, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const TeamList = () => {
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");
  const [teamDetails, setTeamDetails] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useTeamTheme(teamDetails);

  const formatHeight = (h) => {
    if (h === null || h === undefined) return "N/A";
    const n = Number(h);
    if (Number.isNaN(n)) return h;
    const feet = Math.floor(n / 12);
    const inches = n % 12;
    return `${feet}'${inches}"`;
  };

  const formatWeight = (w) => {
    if (!w) return "N/A";
    return `${w} lbs`;
  };

  const fetchTeamDetails = async (id) => {
    setError("");
    setTeamDetails(null);
    setRoster([]);
    if (!id) return;
    setLoading(true);
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      let url = `${API_URL}/api/teams/${id}/roster`;
      if (activeSaveId) {
        url += `?save_id=${activeSaveId}`;
      }
      
      const res = await fetch(url);
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Failed to load team");
        return;
      }
      const data = await res.json();
      setTeamDetails(data.team || null);
      setRoster(data.roster || []);
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const teamsUrl = activeSaveId
          ? `${API_URL}/api/teams?save_id=${activeSaveId}`
          : `${API_URL}/api/teams`;
        const res = await fetch(teamsUrl);
        if (!res.ok) {
          const data = await res.json();
          setError(data.detail || "Failed to load teams");
          return;
        }
        const data = await res.json();
        const list = data.teams || [];
        setTeams(list);
      } catch (err) {
        setError(err.message || "Server error");
      }
    };
    fetchTeams();
  }, []);

  useEffect(() => {
    const id = searchParams.get("teamId");
    if (id && id !== selected) {
      setSelected(id);
      fetchTeamDetails(id);
    }
  }, [searchParams]);

  return (
    <>
      <Navbar />
      <div className="teamlist-container">
        {/* Top Bar - Simplified */}
        <div className="teamlist-topbar">
          <div className="topbar-content">
            <h1 className="page-title">NBA Teams</h1>
          </div>
        </div>

      {/* Team Selector */}
      <div className="team-selector-bar">
        <label htmlFor="teamSelect" className="selector-label">Select Team:</label>
        <select
          id="teamSelect"
          className="team-dropdown"
          value={selected}
          onChange={async (e) => {
            const id = e.target.value;
            setSelected(id);
            await fetchTeamDetails(id);
          }}
        >
          <option value="">-- Choose a team --</option>
          {teams.map((t) => (
            <option key={t._id} value={t._id}>
              {t.name} {t.city ? `(${t.city})` : ""}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠</span>
          <p>{error}</p>
        </div>
      )}

      {/* Main Content Area */}
      {!selected && !loading && (
        <div className="empty-state">
          <div className="empty-icon">🏀</div>
          <h2>Select a Team to View Roster</h2>
          <p>Choose a team from the dropdown above to see their details and player roster</p>
        </div>
      )}

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading team data...</p>
        </div>
      )}

      {teamDetails && !loading && (
        <div className="content-layout">
          {/* Left: Team Details (40%) */}
          <div className="team-details-section">
            <div className="team-card">
              <div className="team-card-header">
                <div className="team-logo">
                  <span className="team-initial">{teamDetails.name?.charAt(0)}</span>
                </div>
                <div className="team-name-block">
                  <h2 className="team-name">{teamDetails.name}</h2>
                  {teamDetails.city && <p className="team-location">{teamDetails.city}</p>}
                </div>
              </div>

              <div className="team-stats">
                <div className="stat-box">
                  <div className="team-stat-label">Wins</div>
                  <div className="team-stat-value wins-value">{teamDetails.wins || 0}</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-box">
                  <div className="team-stat-label">Losses</div>
                  <div className="team-stat-value losses-value">{teamDetails.losses || 0}</div>
                </div>
              </div>

              <div className="team-record-bar">
                <span className="record-text">
                  {teamDetails.wins || 0}W - {teamDetails.losses || 0}L
                </span>
                <span className="win-pct">
                  {((teamDetails.wins || 0) / ((teamDetails.wins || 0) + (teamDetails.losses || 0) || 1) * 100).toFixed(1)}%
                </span>
              </div>

              <div className="team-actions">
                <Link to={`/teams/${teamDetails._id}/schedule`} className="action-button">
                  📅 View Schedule
                </Link>
              </div>

              <div className="team-info-footer">
                <div className="info-item">
                  <span className="info-label">Roster Size</span>
                  <span className="info-value">{roster.length}/15 Players</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Player Roster (60%) */}
          <div className="roster-section">
            <div className="roster-header">
              <h3 className="roster-title">Team Roster</h3>
              <span className="roster-count">{roster.length}/15 Players</span>
            </div>

            {roster.length === 0 ? (
              <div className="roster-empty">
                <p>No players found on this roster</p>
              </div>
            ) : (
              <div className="roster-list">
                {[...roster].sort((a, b) => {
                  return playerOverall(b) - playerOverall(a);
                }).map((player) => (
                  <Link
                    key={player._id}
                    to={`/players/${player._id}`}
                    state={{ fromPage: `/teams?teamId=${selected}` }}
                    className="player-row"
                  >
                    <div className="player-row-left">
                      <span className="player-index">#{player.number ?? "?"}</span>
                      <div className="player-avatar">
                        <span className="avatar-text">{player.position || "?"}</span>
                      </div>
                      <div className="player-name-block">
                        <h4 className="player-name">
                          {player.displayName || player.name || "Unknown Player"}
                        </h4>
                        <span className="player-position">{player.position || "N/A"}</span>
                      </div>
                    </div>

                    <div className="player-row-right">
                      <div className="player-stat">
                        <span className="player-stat-label">OVR</span>
                        <span className="player-stat-value">{playerOverall(player)}</span>
                      </div>
                      <div className="player-stat">
                        <span className="player-stat-label">HT</span>
                        <span className="player-stat-value">{formatHeight(player.height)}</span>
                      </div>
                      <div className="player-stat">
                        <span className="player-stat-label">WT</span>
                        <span className="player-stat-value">{formatWeight(player.weight)}</span>
                      </div>
                      {player.stats && (
                        <>
                          <div className="player-stat">
                            <span className="player-stat-label">PPG</span>
                            <span className="player-stat-value">{player.stats.ppg || "N/A"}</span>
                          </div>
                          <div className="player-stat">
                            <span className="player-stat-label">RPG</span>
                            <span className="player-stat-value">{player.stats.rpg || "N/A"}</span>
                          </div>
                          <div className="player-stat">
                            <span className="player-stat-label">APG</span>
                            <span className="player-stat-value">{player.stats.apg || "N/A"}</span>
                          </div>
                        </>
                      )}
                      <div className="player-stat">
                        <span className="player-stat-label">Health</span>
                        <span className="player-stat-value" style={{
                          color: player.injuryType && player.injuryGamesRemaining && player.injuryGamesRemaining > 0 ? "#f87171" : "#4ade80"
                        }}>
                          {player.injuryType && player.injuryGamesRemaining && player.injuryGamesRemaining > 0 ? "Injured" : "Healthy"}
                        </span>
                      </div>
                      <div className="player-arrow">→</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default TeamList;
